import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions, imagePath, imgTypes, SnackType } from '@global/GlobalFunctions';
import { Account, IAccount, IAccountUser } from '@services/accounts.service';
import { Action, ActionEdit, ActionsDataService, IActionType } from '@services/actions-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EventComment, EventsDataService, RuleEvent, TimelineItem } from '@services/events-data.service';
import { Rule, RuleDefinition, RuleEdit, RulesDataService } from '@services/rules-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { TimelineItemModel } from '@syncfusion/ej2-angular-layouts';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';
import { CompleteActionComponent } from 'src/app/dialogs/complete-action/complete-action.component';
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { Location } from '@angular/common';
import { CreateActionComponent, createActionDialogData } from 'src/app/dialogs/create-action/create-action.component';
import { ActionEditsComponent, ActionEditsDialogData } from 'src/app/dialogs/action-edits/action-edits.component';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Default Class
 */
@Component({
	selector: 'app-action-details',
	templateUrl: './action-details.component.html',
	styleUrls: ['./action-details.component.scss']
})
export class ActionDetailsComponent implements OnDestroy {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** comments */
	public comments: EventComment[];

	/** action */
	public action: Action;

	/** asset */
	public asset: LevelAsset;

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = []

	/** thisUser */
	public thisUser: WebAppUser;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** rule */
	public rule: Rule;

	/** event */
	public event: RuleEvent;

	/** acknowledged */
	public Acknowledged: boolean = false;

	/** attachedToEvent */
	public attachedToEvent: boolean = true;

	/** Timeline */
	public Timeline: TimelineItem[] = [];

	/** Debugging */
	public displayTimeline: boolean = true;

	/** Whether to automatically open the comments accordion */
	public expanded: boolean = false;

	/** now */
	public now: number = new Date().getTime();

	/** disabledRules */
	public disabledRules: any[] = [];

	/** visiblePropertyNames - Git#103 */
	public visiblePropertyNames: { propName: string, visibleName: string, function?: any, funcParam?: any }[] = [
		{ propName: "ActionID", visibleName: "Action ID" },
		{ propName: "ActionType", visibleName: "Action Type", function: this.getActionTypeFromAction },
		{ propName: "AssetID", visibleName: "Asset ID" },
		{ propName: "AssetName", visibleName: "Asset Name" },
		{ propName: "EventID", visibleName: "Event ID" },
		{ propName: "TimeLineID", visibleName: "Timeline ID" },
		{ propName: "Timestamp", visibleName: "Created At" },
		{ propName: "Completed", visibleName: "Completed" },
		{ propName: "Details", visibleName: "Details" },
		{ propName: "CompletedTimestamp", visibleName: "Completed At" },
		{ propName: "CompletedByUserID", visibleName: "Completed By" },
		{ propName: "CreatedByUserSub", visibleName: "Created By" },
		{ propName: "AssignedToUserSub", visibleName: "Assigned To", function: this.getUserName },
		{ propName: "WorkOrderReference", visibleName: "Work Order Reference" },
		{ propName: "TargetCompletionTimestamp", visibleName: "Target Completion Date" }
	];

	/** actionEdits - Git#103 */
	public actionEdits: ActionEdit[] = [];

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private location: Location
	) {
		combineLatest([
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.paramMap.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, paramMap, queryParamMap, actionTypes]) =>
				!!currentUser && !!paramMap && !!queryParamMap && !!actionTypes)
		).subscribe({
			next: async ([currentUser, paramMap, queryParamMap, actionTypes]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;
				this.actionTypes = actionTypes

				if (this.actionTypes) { // issue `#103
					let _propData = this.visiblePropertyNames.find((x) => x.propName == "ActionType");
					if (_propData) _propData.funcParam = this.actionTypes;
				}

				this.thisUser = currentUser;

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				let _actionID = paramMap.get("actionID");

				let actionID: number = 0;

				if (_actionID) {
					actionID = parseInt(_actionID);
				}

				let expanded = queryParamMap.get('expanded');
				if (expanded == "true") {
					this.expanded = true;
				}

				this.getAction(actionID);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getAction
	 */
	getAction(actionID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.actionsDataService.getActions$(this.accountGUID, this.organisationUID, actionID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (actions) => {
				this.action = actions[0];
				if (this.action.DisabledRules) {
					this.disabledRules = JSON.parse(this.action.DisabledRules);
				}
				if (this.action.Completed) {
					this.Acknowledged = true;
				} else {
					this.Acknowledged = false;
				}
				if (this.action.EventID == 0) {
					this.attachedToEvent = false;
				}
				if (this.action.TimeLineID == 0) {
					this.displayTimeline = false;
				}
				if (this.attachedToEvent) {
					this.getEvent();
				}
				if (this.displayTimeline) {
					this.getTimeline();
				}
				this.getAsset();
				this.getEventComments();
				this.getActionEdits(); // Git#103
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.actionsDataService.getActions$", false); }
		});
	}

	/**
	 * getAsset
	 */
	getAsset() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.organisationUID, this.action.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (asset) => {
				this.asset = asset;
			},
			error: (error) => {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false); }
			}
		});
	}

	/**
	 * getEvent
	 */
	getEvent() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.eventsDataService.getRuleEvents$(this.accountGUID, this.organisationUID, 0, this.action.EventID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (event) => {
				this.event = event[0];
				this.getRule();
			},
			error: (error) => {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getRuleEvents$", false); }
			}
		});
	}

	/**
	 * getRule
	 */
	async getRule() {

		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountGUID, this.organisationUID, 0, this.event.RuleID)
			);
			if (rules?.length) {
				this.rule = rules[0]
			}
		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false);
		}
	}

	/**
		 * convertEpoch
		 * @param epoch
		 * @returns
		 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/** Return to the events page on the correct tab */
	goToActions() {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/actions`]);
	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * getActionTypeFromValue
	 * @param value value
	 * @returns
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * getActionTypeFromValue
	 * @param value value
	 * @returns
	 */
	getActionTypeFromAction(action: Action, overrideActionTypes?: IActionType[]): string {
		let result = undefined;
		if (overrideActionTypes) {
			result = overrideActionTypes.find((x: IActionType) => x.ActionTypeID == action.ActionType)?.Description;
		}
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * goToEvent
	 */
	goToEvent() {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/event/${this.action.EventID}`], { queryParams: { eventType: 2 } });
	}

	/** addCommentNew */
	addCommentNew(acknowledgeAfterComment?: boolean, reopenAfterComment?: boolean) {
		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "Add Comment",
				"DialogText": "Add a Comment?"
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// Perform the delete action with the comment
			// console.log('The dialog was closed with comment:', result.comment);
			// Add your delete logic here
			let _comment = result.comment;

			const fetchDataSubject = new Subject<void>();

			let comment: EventComment = {
				CommentText: _comment,
				EventID: this.action.ActionID,
				EventType: 100
			}

			this.eventsDataService.postEventComment$(this.accountGUID, this.organisationUID, comment.EventID, comment, 100, this.thisUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.getEventComments(acknowledgeAfterComment, reopenAfterComment);
					this.globalFunctions.DisplaySnackBar({
						message: "Comment Added",
						type: SnackType.Info,
						duration: 1000
					});
					if (this.displayTimeline) {
						this.getTimeline();
					}
				}
			});
		});
	}

	/** getEventComments */
	getEventComments(acknowledgeAfterComment?: boolean, reopenAfterComment?: boolean) {
		this.comments = [];
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.eventsDataService.getEventComment$(this.accountGUID, this.organisationUID, this.action.ActionID, 100).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false)
			})
		).subscribe({
			next: (comments: any) => {
				this.comments = comments;
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "eventDetails Error: Retrieving Rule Event Comments", false)
				}
			},
		});
	}

	/**
	 * OpenRule
	 * @param rule rule
	 */
	OpenRule(rule: Rule) {
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || "",
			readonly: !this.selectedAccount.Admin
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			/* split the rule into the rule-definition composition */

			if (rule.RuleDef) {
				const data1 = {
					RuleDefID: rule.RuleDef[0].RuleDefID,
					RuleID: rule.RuleID,
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					const data2 = {
						RuleDefID: rule.RuleDef[1].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					const data2 = {
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}
			this.patchRule(rule)
		});
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr timeStr
	 * @returns total minutes
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * patchRule
	 * @param rule rule
	 */
	patchRule(rule: Rule) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.patchRule$(this.accountGUID,
			this.organisationUID, rule.AssetID, rule, this.thisUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((sqlResult) => !!sqlResult),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (sqlResult: any) => {
					// console.log(sqlResult)
					this.globalFunctions.DisplaySnackBar({
						message: "Rule Updated",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false);
					}
				},
			});
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes totalMinutes
	 * @returns HH:MM string
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * completeAction
	 * @param addComment addComment
	 */
	completeAction(addComment: boolean, enableRule: boolean, createFurtherAction: boolean = false) {
		if (addComment) {
			// this.addCommentNew(true);
			this.openCompleteActionDialog();
		} else {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.actionsDataService.completeAction$(this.accountGUID, this.organisationUID, this.action.ActionID, this.thisUser.UserID, enableRule).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result: any) => {
					this.globalFunctions.DisplaySnackBar({
						message: "Action Marked as Complete",
						type: SnackType.Info,
						duration: 2000
					});
					this.Acknowledged = true;
					this.getAction(this.action.ActionID);
					if (createFurtherAction) {
						this.createFurtherAction();
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "eventDetails Error: Retrieving Rule Event Comments", false)
					}
				},
			});
		}
	}

	/**
	 * openCompleteActionDialog
	 */
	openCompleteActionDialog() {
		// if (this.rule && this.disabledRules.length == 0) {
		// 	this.disabledRules = [this.rule];
		// }
		let dialogRef;
		dialogRef = this.dialog.open(CompleteActionComponent, {
			width: '500px',
			data: {
				"HeaderText": "Complete Action",
				"DialogText": "Add a comment and select what should be done with any rules.",
				"attachedToEvent": true,
				"DisabledRules": this.disabledRules
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let _comment = result.comment;

			const fetchDataSubject = new Subject<void>();

			let comment: EventComment = {
				CommentText: _comment,
				EventID: this.action.ActionID,
				EventType: 100
			}

			let enableRule: boolean = false;
			let delay: number = 30;
			if (result.ruleChoice == 1) {
				enableRule = true;
			} else if (result.ruleChoice == 2) {
				delay = result.delay;
			}

			let createFurtherAction: boolean = false;
			if (result.createFurtherAction) {
				createFurtherAction = true;
			}

			this.eventsDataService.postEventComment$(this.accountGUID, this.organisationUID, comment.EventID, comment, 100, this.thisUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.getEventComments(true);
					this.globalFunctions.DisplaySnackBar({
						message: "Comment Added",
						type: SnackType.Info,
						duration: 1000
					});
					this.completeAction(false, enableRule, createFurtherAction);
					if (result.ruleChoice == 2) {
						this.disabledRules.forEach(rule => {
							this.createRuleReminder(rule, delay);
						});
					}
				}
			});
		});
	}

	public timelineItems: TimelineItemModel[] = [];

	/**
	 * getTimeline
	 */
	getTimeline() {
		const fetchDataSubject = new Subject<void>();

		this.eventsDataService.getTimeline$(this.accountGUID, this.organisationUID, this.action.TimeLineID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((timeline) => !!timeline),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (timeline) => {
				this.Timeline = JSON.parse(timeline[0].Data);
				this.timelineItems = this.Timeline.map(({ Category, Content, Timestamp }) => ({
					dotCss: Category.toString(),
					content: Content,
					oppositeContent: Timestamp.toString(),
					object: JSON.parse(Content)
				}));
			}
		});
	}

	/**
	 * buildObject
	 */
	buildObject(content: string, _category: string): any {
		let category: number = parseInt(_category);
		if (category == 1 || 2 || 3) {
			let result = <RuleEvent[]>JSON.parse(content);
			return result[0];
		} else if (category == 4 || 6) {
			let result = <EventComment[]>JSON.parse(content);
			return result[0];
		} else if (category == 5 || 7) {
			let result = <Action[]>JSON.parse(content);
			return result[0];
		} else if (category == 8 || 9) {
			let result = <Rule[]>JSON.parse(content);
			return result[0];
		} else if (category == 10) {
			let result = <RuleEdit[]>JSON.parse(content);
			return result[0];
		} else if (category == 11) {
			let result = <ActionEdit[]>JSON.parse(content);
			return result[0];
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpochString(_epoch: string): string {
		/** Use the Global Function */
		let epoch: number = parseInt(_epoch);
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToTimelineItem
	 * @param timelineItem timelineItem
	 */
	goToTimelineItem(timelineItem: any) {
		let category: number = parseInt(timelineItem.dotCss);
		if (category == 1 || category == 2 || category == 3) {
			let result = <RuleEvent[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/event/${result[0].RuleEventID}`],
				{ queryParams: { eventType: 2 } }
			);
		} else if (category == 4) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/event/${result[0].EventID}`],
				{ queryParams: { eventType: 2, expanded: true } }
			);
		} else if (category == 6) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${result[0].EventID}`],
				{ queryParams: { expanded: true } }
			);
		} else if (category == 5 || category == 7) {
			let result = <Action[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${result[0].ActionID}`]
			);
		} else if (category == 8 || category == 9) {
			let result = <Rule[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/rules/${result[0].RuleID}`]
			);
		} else if (category == 10) {
			let result = <RuleEdit[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/rules/${result[0].RuleID}`]
			);
		} else if (category == 11) {
			let result = <ActionEdit[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${result[0].ActionID}`]
			);
		}
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * createRuleReminder
	 * @param rule rule
	 * @param delay delay
	 */
	createRuleReminder(rule: Rule, delay: number) {
		if (rule.RuleID) {
			const fetchDataSubject = new Subject<void>();

			this.rulesDataService.postRuleReminder$(this.accountGUID, this.organisationUID, rule.RuleID, delay).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((timeline) => !!timeline),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					this.globalFunctions.DisplaySnackBar(
						{
							message: "Reminder Created",
							duration: 1000
						}
					);
				}
			});
		}
	}

	/**
	 * convertEpochNoTime
	 * @param epoch epoch
	 */
	convertEpochNoTime(epoch: number): string {
		let arr = convertEpochToDateString(epoch).split(" ");
		arr.pop();
		let result: string = "";
		arr.forEach(item => {
			result = result.concat(item, " ");
		})
		return result;
	}

	/**
	 * checkIfOverdue
	 * @returns
	 */
	checkIfOverdue(): boolean {
		let result: boolean = false;
		if (this.action.TargetCompletionTimestamp != undefined && (this.action.TargetCompletionTimestamp + (1 * 24 * 60 * 60 * 1000)) < this.now) {
			if (!this.action.Completed) {
				result = true;
			}
		}
		return result;
	}

	/**
	 * editAction
	 * @param action action
	 */
	editAction(action: Action) {
		let dialogData = <createActionDialogData>{
			assetItem: this.asset,
			isAttachedtoEvent: this.attachedToEvent,
			editing: true,
			action: this.action
		}

		if (this.attachedToEvent) {
			dialogData.event = this.event;
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (result) {
				this.action.WorkOrderReference = result.workOrderReference;
				this.action.Details = result.details;
				this.action.AssignedToUserSub = result.assignedToUser;
				this.action.ActionType = result.actionType;

				const fetchDataSubject = new Subject<void>();

				this.actionsDataService.patchAction$(this.accountGUID, this.organisationUID, this.action.ActionID, this.action).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((action) => !!action),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (action) => {
						this.globalFunctions.DisplaySnackBar({
							message: "Action Changed",
							type: SnackType.Info,
							duration: 1000
						});
						this.getAction(this.action.ActionID);
					}
				});
			}
		});
	}

	/**
	 * checkNotLast
	 * @param data data
	 * @returns
	 */
	checkNotLast(data: any): boolean {
		let result: boolean = false;
		let finalItem: number = this.Timeline.length - 1;
		if (data.itemIndex == finalItem) {
			result = true;
		}
		return result;
	}

	/**
	 * getChangedPropeties
	 * @param actionEdit actionEdit 
	 */
	getChangedPropeties(actionEdit: ActionEdit): { name: string, old: string, new: string }[] {
		const previousAction: Action = JSON.parse(actionEdit.PreviousAction)[0];
		const newAction: Action = JSON.parse(actionEdit.NewAction)[0];

		const differingProperties: { name: string, old: string, new: string }[] = [];

		Object.entries(newAction).forEach((prop) => {
			const propLabel: keyof Action = prop[0] as keyof Action;
			const oldProp = previousAction[propLabel];
			if (oldProp != undefined && prop[1] != oldProp) {
				const _propData = this.visiblePropertyNames.find((x) => x.propName == propLabel);
				if (_propData) {
					differingProperties.push({
						name: _propData.visibleName,
						old: !_propData.function ? oldProp.toString() : _propData.function(previousAction, _propData.funcParam),
						new: !_propData.function ? prop[1] : _propData.function(newAction, _propData.funcParam)
					});
				}
			}
		});

		return differingProperties;
	}

	/**
	 * getUserName
	 * @param sub sub 
	 */
	getUserName(action: Action): string {
		return action.AssignedToUserName ?? "";
	}

	/**
	 * createFurtherAction
	 */
	createFurtherAction() {
		let dialogData = <createActionDialogData>{
			assetItem: this.asset
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (!result) {
				this.createFurtherAction();
			} else {
				if (!result.skipAction) {
					let newAction: Action = {
						ActionID: 0,
						ActionType: result.actionType,
						AssetID: this.asset.AssetID,
						AssetName: `${this.asset.Name} - ${this.asset.Description}`,
						EventID: 0,
						TimeLineID: this.action.TimeLineID,
						Timestamp: 0,
						Completed: result.isHistoric,
						Details: result.details,
						CompletedTimestamp: result.isHistoric ? result.targetCompletionTimestamp.getTime() : 0,
						CompletedByUserID: result.isHistoric ? result.assignedToUser : '',
						CreatedByUserSub: '',
						AssignedToUserSub: result.assignedToUser,
						WorkOrderReference: result.workOrderReference,
						TargetCompletionTimestamp: result.targetCompletionTimestamp.getTime(),
						DisabledRules: result.ruleList ? result.ruleList.toString() : ''
					}
					let disable;
					if (result.ruleList && result.ruleList.length > 0) {
						disable = true;
					}

					const fetchDataSubject = new Subject<void>();

					this.actionsDataService.postAction$(this.accountGUID, this.organisationUID, newAction, disable).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((action) => !!action),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (action) => {
							this.globalFunctions.DisplaySnackBar({
								message: "Further Action Created",
								type: SnackType.Info,
								duration: 1000
							});
							this.getTimeline();
						}
					});
				}
			}
		});
	}

	/**
	 * getActionEdits
	 */
	getActionEdits() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.actionsDataService.getActionEdits$(this.accountGUID, this.organisationUID, this.action.ActionID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((edits) => !!edits),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (edits) => {
				this.actionEdits = edits;
			},
			error: (error) => {
				if (error.status == 404) {
					this.actionEdits = [];
				}
				else { this.globalFunctions.HandleError(error, "Error: this.actionsDataService.getActionEdits$", false); }
			}
		});
	}

	/**
	 * showActionEdits
	 */
	showActionEdits() {
		const data: ActionEditsDialogData = {
			action: this.action,
			actionEdits: this.actionEdits,
			visiblePropertNames: this.visiblePropertyNames
		}
		const dialogRef = this.dialog.open(ActionEditsComponent, {
			data: data
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {

		});
	}
}
